import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AsyncSelect, type StylesConfig } from '@databases/atlaskit/Select';
import { type FieldTypeWithOnlyValues } from '@databases/field-value';
import { messages } from '@databases/intl/messages';
import { useIsMounted } from '@databases/react';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { UserCard, AvatarWithLabel } from '@databases/user';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit.tsx';
import { baseSelectStyleConfig } from '../popover-edit-common/index.ts';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection.tsx';
import { type EditComponentProps } from '../types.ts';
import { withJiraWorkItemCheck } from '../withJiraWorkItemCheck.tsx';

import { useJiraAssigneeData } from './JiraAssigneeUtil.ts';
import { getAssignableUsers, type AssigneeUser } from './api.ts';

/** Represents an assignee option in the dropdown */
interface AssigneeOption {
  label: string;
  value: string;
  user?: AssigneeUser; // Optional for "Unassigned" option
}

/** Structure of assignee data from the database field values */
interface CurrentAssigneeData {
  atlassianUserId?: string;
  displayName?: string;
  avatarSource?: string;
  url?: string;
}

/**
 * Creates a custom indicators container with an optional clear button.
 * The clear button only appears when an assignee is selected and prevents
 * the dropdown from opening when clicked.
 */
const createCustomIndicatorsContainer = (
  handleClear: () => Promise<void>,
  shouldShowClearButton: boolean,
  isMenuOpen: boolean
) => {
  const IndicatorsContainer: FunctionComponent<{ children: React.ReactNode }> = ({
    children
  }) => {
    const handleClearClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      await handleClear();
    };

    const preventDropdownOpen = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    if (isMenuOpen) {
      return (
        <>
          <Box
            style={{
              position: 'absolute',
              left: token('space.150', '12px'),
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <SearchIcon label="" size="medium" />
          </Box>
          <div />
        </>
      );
    }
    return (
      <Flex alignItems="center">
        {shouldShowClearButton && (
          <Box
            paddingInline="space.050"
            onClick={handleClearClick}
            onMouseDown={preventDropdownOpen}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            testId="jira-assignee-clear-button"
          >
            <CrossIcon label="" size="small" />
          </Box>
        )}
        {children}
      </Flex>
    );
  };

  IndicatorsContainer.displayName = 'JiraAssigneeIndicatorsContainer';
  return IndicatorsContainer;
};

const assigneeSelectStyles: StylesConfig<AssigneeOption, false> = {
  singleValue: (base) => ({
    ...base,
    div: {
      alignItems: 'center'
    },
    padding: 0,
    margin: 0
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: token('space.150', '12px'),
    paddingRight: token('space.150', '12px'),
    paddingBottom: token('space.075', '6px'),
    paddingTop: token('space.075', '6px')
  }),
  control: (base) => ({
    ...base,
    padding: 0,
    margin: 0,
    border: 0
  }),
  option: (base) => ({
    ...base,
    div: {
      alignItems: 'center'
    }
  })
};

interface AssigneeEditFieldProps {
  issueRef: JiraIssueRef;
  assigneeItem?: FieldTypeWithOnlyValues;
  resolvedIssue: ResolvedJiraIssueRef | undefined;
  updateAssignee: (issueRef: JiraIssueRef, assigneeAccountId: string) => Promise<void>;
  isUpdating: boolean;
  issueRefs: JiraIssueRef[];
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
}

const AssigneeEditFieldInline: FunctionComponent<AssigneeEditFieldProps> = ({
  issueRef,
  assigneeItem,
  resolvedIssue,
  updateAssignee,
  isUpdating,
  issueRefs,
  onFocus,
  onBlur,
  autoFocus
}) => {
  const intl = useIntl();
  const isMountedRef = useIsMounted();
  const [pendingSelection, setPendingSelection] = useState<AssigneeOption | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const shouldPreload = issueRefs?.length === 1;
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const shouldAutoOpen = shouldPreload && !hasAutoOpened;

  const [isPreloading, setIsPreloading] = useState(false);
  const hasPreloadedRef = useRef(false);
  const preloadedOptionsRef = useRef<AssigneeOption[]>([]);

  useEffect(() => {
    setHasAutoOpened(false);
    hasPreloadedRef.current = false;
    preloadedOptionsRef.current = [];
    setIsMenuOpen(false);
    setHasBeenOpened(false);
    setIsPreloading(false);
  }, [issueRef?.key, issueRef?.ari]);

  useEffect(() => {
    if (shouldAutoOpen) {
      setIsMenuOpen(true);
      setHasBeenOpened(true);
    }
  }, [shouldAutoOpen]);

  const UNASSIGNED_LABEL = intl.formatMessage(messages['jira-assignee.unassigned.label']);

  const loadOptions = useCallback(
    async (query: string): Promise<AssigneeOption[]> => {
      if (isClearing) {
        return [];
      }

      if (hasPreloadedRef.current && !query) {
        return preloadedOptionsRef.current;
      }

      if (!issueRef || !resolvedIssue?.ari) {
        return [];
      }

      try {
        const users = await getAssignableUsers({
          issueAri: resolvedIssue.ari,
          query,
          intl
        });

        const userOptions: AssigneeOption[] = users.map((user) => ({
          label: user.displayName,
          value: user.accountId,
          user
        }));

        // Add "Unassigned" option if it matches the search query
        const shouldShowUnassigned =
          query === '' || UNASSIGNED_LABEL.toLowerCase().includes(query.toLowerCase());

        if (shouldShowUnassigned) {
          const unassignedOption: AssigneeOption = {
            label: UNASSIGNED_LABEL,
            value: ''
          };
          userOptions.unshift(unassignedOption);
        }

        if (!query && isMountedRef.current) {
          preloadedOptionsRef.current = userOptions;
          hasPreloadedRef.current = true;
        }

        return userOptions;
      } catch {
        // Error is handled in the parent component
        return [];
      }
    },
    [issueRef, resolvedIssue, intl, UNASSIGNED_LABEL, isClearing, isMountedRef]
  );

  useEffect(() => {
    if (
      issueRefs?.length === 1 &&
      issueRef &&
      resolvedIssue?.ari &&
      !hasPreloadedRef.current
    ) {
      let isCancelled = false;
      setIsPreloading(true);

      loadOptions('').finally(() => {
        if (!isCancelled && isMountedRef.current) {
          setIsPreloading(false);
        }
      });

      return () => {
        isCancelled = true;
        setIsPreloading(false);
      };
    }
  }, [issueRefs?.length, issueRef, resolvedIssue, loadOptions, isMountedRef]);

  const formatOptionLabel = useCallback((option: AssigneeOption): ReactNode => {
    if (!option.user) {
      // undefined will get the icon from ADS
      return <AvatarWithLabel userOrGroup={undefined} label={option.label} />;
    }

    return (
      <UserCard
        accountId={option.user.accountId}
        displayName={option.user.displayName}
        variant="avatar"
      />
    );
  }, []);

  const handleChange = useCallback(
    async (option: AssigneeOption | null) => {
      if (resolvedIssue?.ari && option) {
        setPendingSelection(option);

        try {
          await updateAssignee({ ...issueRef, ari: resolvedIssue.ari }, option.value);
        } finally {
          setPendingSelection(null);
        }
      }
    },
    [issueRef, resolvedIssue, updateAssignee]
  );

  const currentValue = useMemo((): AssigneeOption | undefined => {
    if (isUpdating && pendingSelection !== null) {
      return pendingSelection;
    }

    // If there's no assignee data or the assignee is empty, show "Unassigned"
    if (!assigneeItem?.values?.[0]) {
      return {
        label: UNASSIGNED_LABEL,
        value: ''
      };
    }

    const currentAssignee = assigneeItem.values[0] as CurrentAssigneeData;

    if (!currentAssignee.atlassianUserId || currentAssignee.atlassianUserId === '') {
      return {
        label: UNASSIGNED_LABEL,
        value: ''
      };
    }

    return {
      label: currentAssignee.displayName || '',
      value: currentAssignee.atlassianUserId || '',
      user: {
        accountId: currentAssignee.atlassianUserId || '',
        displayName: currentAssignee.displayName || '',
        avatarUrl: currentAssignee.avatarSource || ''
      }
    };
  }, [isUpdating, pendingSelection, assigneeItem, UNASSIGNED_LABEL]);

  /**
   * Handles clearing the assignee by setting it to unassigned.
   * Uses the same flow as normal selection to ensure consistent UX.
   */
  const handleClear = useCallback(async (): Promise<void> => {
    if (resolvedIssue?.ari && currentValue?.value !== '') {
      const unassignedOption: AssigneeOption = {
        label: UNASSIGNED_LABEL,
        value: ''
      };

      setIsClearing(true);
      setPendingSelection(unassignedOption);

      try {
        await updateAssignee(
          { ...issueRef, ari: resolvedIssue.ari },
          '' // Empty string means unassigned
        );
      } finally {
        setPendingSelection(null);
        setIsClearing(false);
      }
    }
  }, [issueRef, resolvedIssue, updateAssignee, currentValue, UNASSIGNED_LABEL]);

  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
    setHasBeenOpened(true);
    if (issueRefs?.length === 1) {
      setHasAutoOpened(true);
    }
  }, [issueRefs?.length]);

  const handleMenuClose = useCallback(() => {
    setIsMenuOpen(false);
    setIsPreloading(false);
  }, []);

  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  const isAssigned = Boolean(currentValue?.value && currentValue.value !== '');
  const shouldShowClearButton = isAssigned && !isUpdating;

  const getSelectStyles = (): StylesConfig<AssigneeOption, false> => {
    if (isMenuOpen) {
      return {
        ...baseSelectStyleConfig,
        ...assigneeSelectStyles,
        control: (base: any) => ({
          ...base,
          display: 'flex',
          flexWrap: 'nowrap',
          justifyContent: 'space-between',
          paddingLeft: token('space.200', '16px'),
          paddingRight: token('space.150', '12px'),
          position: 'relative',
          // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
          font: token('font.body', '14px')
        }),
        placeholder: (base: any) => ({
          ...base,
          left: token('space.200', '16px'),
          paddingLeft: token('space.075', '6px')
        }),
        input: (base: any) => ({
          ...base,
          marginLeft: token('space.075', '6px')
        })
      };
    }

    return {
      ...baseSelectStyleConfig,
      ...assigneeSelectStyles,
      control: (base: any) => ({
        ...base,
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        paddingLeft: token('space.150', '12px'),
        paddingRight: token('space.150', '12px'),
        position: 'relative',
        // Override @atlaskit/select's font.body.large to match the cell's 14px font size.
        font: token('font.body', '14px')
      })
    };
  };

  return (
    <AsyncSelect<AssigneeOption>
      value={isMenuOpen && hasBeenOpened ? null : currentValue}
      autoFocus={autoFocus}
      placeholder={intl.formatMessage(messages['jira-assignee.placeholder.non-final'])}
      loadOptions={loadOptions}
      formatOptionLabel={formatOptionLabel}
      getOptionValue={(option: AssigneeOption) => option.value}
      getOptionLabel={(option: AssigneeOption) => option.label}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      noOptionsMessage={() =>
        intl.formatMessage(messages['jira-assignee.no-options.non-final'])
      }
      isLoading={isUpdating || (isPreloading && (isMenuOpen || shouldAutoOpen))}
      defaultMenuIsOpen={shouldAutoOpen}
      isSearchable={true}
      isClearable={false}
      menuPortalTarget={document.body}
      styles={getSelectStyles()}
      components={{
        IndicatorsContainer: createCustomIndicatorsContainer(
          handleClear,
          shouldShowClearButton,
          isMenuOpen
        )
      }}
    />
  );
};

const JiraAssigneePopover: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
> = ({
  contentId,
  entryId,
  field,
  onFocus,
  onBlur,
  isRenderedInSidePanel,
  autoFocus,
  isBulkEdit
}) => {
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel
  });

  const data = useJiraAssigneeData(contentId, entryId, field);

  const addDataSection = useMemo(
    () => (
      <ul style={{ listStyleType: 'none', padding: '0' }}>
        {data.issueRefs?.map((issueRef: JiraIssueRef, i: number) => (
          <li key={`${issueRef.key || 'issue'}-${i}`}>
            <AssigneeEditFieldInline
              issueRef={issueRef}
              assigneeItem={data.assigneeItems[i]}
              resolvedIssue={data.resolvedIssues[i]}
              updateAssignee={data.updateAssignee}
              isUpdating={data.isUpdating(issueRef)}
              issueRefs={data.issueRefs}
              onFocus={onFocus}
              onBlur={onBlur}
              autoFocus={autoFocus}
            />
          </li>
        ))}
      </ul>
    ),
    [data, onFocus, onBlur, autoFocus]
  );

  if (!data.issueRefs?.length) {
    return null;
  }

  return (
    <PopoverEdit
      isMultiSelect={false}
      hasCurrentData={false}
      addDataSection={addDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

const JiraAssigneeValueEdit = withJiraWorkItemCheck('assignee', JiraAssigneePopover);

export default JiraAssigneeValueEdit;
